import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/20/solid";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import Header from "../../../../components/header";
import {
  ChartOrderByEnum,
  ChartStatusEnum,
  ChartTypeEnum,
} from "../../../../models/chart";
import { AdminChartsState } from "../hooks/use-admin-charts";

type AdminChartsHeaderProps = {
  state: AdminChartsState;
};

const AdminChartsHeader = (props: AdminChartsHeaderProps) => {
  const adminChartsState: AdminChartsState = props.state;

  const getChartTypeName = () => {
    if (adminChartsState.chartType === ChartTypeEnum.GENERAL_ELECTION) {
      return "General";
    } else if (adminChartsState.chartType === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "Partisan";
    } else if (adminChartsState.chartType === ChartTypeEnum.NON_PARTISAN) {
      return "Non-Partisan";
    } else if (
      adminChartsState.chartType === ChartTypeEnum.PRESIDENTIAL_PRIMARY
    ) {
      return "Presidential";
    }
  };

  const getHeaderTitle = () => {
    if (adminChartsState.chartType === ChartTypeEnum.GENERAL_ELECTION) {
      return "Find a General Chart";
    } else if (adminChartsState.chartType === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "Find a Partisan Primary Chart";
    } else if (adminChartsState.chartType === ChartTypeEnum.NON_PARTISAN) {
      return "Find a Non-Partisan Chart";
    } else if (
      adminChartsState.chartType === ChartTypeEnum.PRESIDENTIAL_PRIMARY
    ) {
      return "Find a Presidential Chart";
    }
  };

  return (
    <>
      <Header>
        <div className="container w-full mx-auto">
          <div className="container mx-auto mb-6 text-3xl font-semibold text-white">
            {getHeaderTitle()}
          </div>
          <div className="container flex items-center gap-2 px-6 py-2 mx-auto mb-6 bg-white rounded-full">
            <MagnifyingGlassIcon className="w-8 h-8 text-gray-500" />
            <input
              key="search-input"
              className="w-full m-2 text-lg outline-none"
              placeholder="Search Charts by Municipality, Precinct or Keyword"
              value={adminChartsState.query}
              onChange={(e) => adminChartsState.setQuery(e.target.value)}
            />
          </div>
        </div>
      </Header>
      <div className="p-12 pt-6 pb-6">
        <div className="flex items-center justify-between py-6 border-b">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Listbox
                value={adminChartsState.status}
                onChange={(e) => {
                  adminChartsState.updateStatus(e);
                }}
              >
                <div className="relative h-full">
                  <Listbox.Button className="flex items-center justify-between px-4 py-2 bg-white border rounded-md border-primary-blue min-w-64">
                    <div>
                      Filter by Status:{" "}
                      <span className="pl-2 font-semibold text-primary-blue">
                        {ChartStatusEnum[adminChartsState.status]}
                      </span>
                    </div>
                    <ChevronDownIcon className="w-4 h-4 ml-8" />
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 w-full py-1 mt-2 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {Object.keys(ChartStatusEnum).map((status) => (
                        <Listbox.Option
                          key={`general-chart-status-${status}`}
                          className={({ active }) =>
                            `cursor-pointer select-none relative py-2 pl-10 pr-4 ${active
                              ? "text-blue-900 bg-blue-100"
                              : "text-gray-900"
                            }`
                          }
                          value={status}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${selected ? "font-medium" : "font-normal"
                                  }`}
                              >
                                {
                                  ChartStatusEnum[
                                  status as keyof typeof ChartStatusEnum
                                  ]
                                }
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            <div className="relative">
              <Listbox
                value={adminChartsState.orderBy}
                onChange={(e) => {
                  adminChartsState.updateOrderBy(e);
                }}
              >
                <div className="relative h-full">
                  <Listbox.Button className="flex items-center justify-between px-4 py-2 bg-white border rounded-md border-primary-blue min-w-64">
                    <div>
                      Sort by:{" "}
                      <span className="pl-2 font-semibold text-primary-blue">
                        {ChartOrderByEnum[adminChartsState.orderBy]}
                      </span>
                    </div>
                    <ChevronDownIcon className="w-4 h-4 ml-8" />
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 w-full py-1 mt-2 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {Object.keys(ChartOrderByEnum).map((orderBy) => (
                        <Listbox.Option
                          key={`general-chart-order-${orderBy}`}
                          className={({ active }) =>
                            `cursor-pointer select-none relative py-2 pl-10 pr-4 ${active
                              ? "text-blue-900 bg-blue-100"
                              : "text-gray-900"
                            }`
                          }
                          value={orderBy}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${selected ? "font-medium" : "font-normal"
                                  }`}
                              >
                                {
                                  ChartOrderByEnum[
                                  orderBy as keyof typeof ChartOrderByEnum
                                  ]
                                }
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            {adminChartsState.status === "Completed" && (
              <div className="flex items-center gap-2">
                <input
                  id="showCompletedTestDecks"
                  type="checkbox"
                  checked={adminChartsState.showCompletedTestDecks}
                  className="w-5 h-5 border-primary-blue hover:cursor-pointer"
                  onChange={(e) =>
                    adminChartsState.updateShowCompletedTestDecks(
                      e.target.checked
                    )
                  }
                />
                <label
                  htmlFor="showCompletedTestDecks"
                  className="select-none hover:cursor-pointer"
                >
                  Show Completed Test Decks
                </label>
              </div>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              adminChartsState.startNewChart();
            }}
            className="uppercase inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Create chart
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminChartsHeader;
